import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Trash, WarningCircle, CheckCircle, CircleNotch, ArrowRight, ShieldCheck, ListChecks,
  Buildings, CreditCard, Calendar, Truck, Factory, Paperclip, Eye,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Toaster, toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { Shield } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const todayISO = () => new Date().toISOString().slice(0, 10);

// Oct 7 2026, user's explicit, literal business rule: Plant P1 buys
// FROM the Plant P2 legal entity (Radish Technologies) acting as
// supplier, and vice versa - these codes are display-only here, the
// backend independently re-derives them server-side from `plant`
// alone and never trusts anything else this form sends (same defense-
// in-depth pattern as the R1766 supplier override).
const PLANT_CONFIG = {
  P1: { company: "RI", billTo: "P1-FIN", supplierCode: "RAD-P2-S", supplierName: "Radish Technologies ALIGARH P2" },
  P2: { company: "RT", billTo: "P2-FIN", supplierCode: "RAY-P1-S", supplierName: "Ray International Aligarh P1" },
};

const fmtMoney = (amount, ccy) =>
  new Intl.NumberFormat(ccy === "USD" ? "en-US" : "en-IN", { style: "currency", currency: ccy === "USD" ? "USD" : "INR", maximumFractionDigits: 2 }).format(amount || 0);

const emptyLine = () => ({
  key: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  product_id: "",
  description: "",
  unit_of_measure: "EA",
  quantity: "",
  unit_price: "",
  delivery_date: todayISO(),
  productQuery: "",
  productSuggestions: [],
  showSuggestions: false,
});

export default function InterplantPurchaseOrderPage() {
  const navigate = useNavigate();
  const [plant, setPlant] = useState("");
  const [poDate, setPoDate] = useState(todayISO());
  const [currency, setCurrency] = useState("INR");
  const [lines, setLines] = useState([emptyLine()]);
  const productDebounceRefs = useRef({});
  const productInputRefs = useRef({});
  const [suggestBoxPos, setSuggestBoxPos] = useState({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { po_number } | { error }

  // Oct 7 2026, user's explicit ask: "add a mandatory attachment of the
  // email" - uploads immediately on file select (same pattern as the
  // Supplier Portal document uploads), stores the returned Object
  // Storage path/filename, and that path is what actually gets sent on
  // Create (never the raw file again) - backend independently re-
  // enforces this isn't empty (Pydantic min_length=1), never trusting
  // a frontend-only "required" attribute for something audit-sensitive.
  const [attachment, setAttachment] = useState(null); // { path, filename, uploading, error }
  const [viewingAttachment, setViewingAttachment] = useState(false);

  const onAttachmentSelect = async (file) => {
    if (!file) return;
    setAttachment({ filename: file.name, uploading: true, path: null, error: null });
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}/interplant-purchase-orders/upload-attachment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachment({ filename: data.attachment_filename, path: data.attachment_path, uploading: false, error: null });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setAttachment({ filename: file.name, uploading: false, path: null, error: typeof detail === "string" ? detail : "Upload failed" });
    }
  };

  // Feb 2026, user's explicit ask: "add a view attachment in the
  // interplant PO creation so that user can confirm attached document" -
  // re-opens the just-uploaded file (by its Object Storage path - no
  // po_number exists yet at this stage) so the user can double-check
  // it's the right approval email before submitting to SAP. Blob fetch
  // (not a raw <a href>) since the endpoint needs the session cookie
  // and returns binary content - same pattern as the Created POs list's
  // own viewInterplantAttachment (there, after the PO already exists).
  const viewAttachment = async () => {
    if (!attachment?.path) return;
    setViewingAttachment(true);
    try {
      const resp = await axios.get(`${API}/interplant-purchase-orders/preview-attachment`, {
        params: { path: attachment.path, filename: attachment.filename },
        responseType: "blob",
      });
      window.open(window.URL.createObjectURL(resp.data), "_blank");
    } catch (e) {
      toast.error("Could not open the attachment - please re-attach the file");
    } finally {
      setViewingAttachment(false);
    }
  };

  // Oct 7 2026, real user report ("many match result show but i can't
  // check correct one") - this dropdown used to be position:absolute
  // INSIDE the line-items table's `overflow-x-auto` wrapper, which per
  // the CSS spec also makes the wrapper clip vertical overflow (any
  // non-"visible" overflow-x forces overflow-y to compute as "auto" on
  // the same box) - so with many matches the list got silently cropped
  // to a tiny sliver instead of showing its own max-h-56 scroll area,
  // making it look broken/unreadable. Rendered via a portal straight to
  // <body> with a measured fixed position instead, so it always
  // escapes that ancestor's clipping no matter how many rows match.
  const positionSuggestBox = (lineKey) => {
    const el = productInputRefs.current[lineKey];
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxBoxHeight = 320; // matches max-h-80
    const spaceBelow = window.innerHeight - r.bottom;
    const openAbove = spaceBelow < maxBoxHeight && r.top > spaceBelow;
    setSuggestBoxPos((prev) => ({
      ...prev,
      [lineKey]: {
        left: r.left, width: Math.max(r.width, 320),
        ...(openAbove
          ? { bottom: window.innerHeight - r.top + 4, maxHeight: Math.max(160, r.top - 12) }
          : { top: r.bottom + 4, maxHeight: Math.max(160, spaceBelow - 12) }),
      },
    }));
  };

  useEffect(() => {
    const closeAll = () => setLines((prev) => prev.map((l) => (l.showSuggestions ? { ...l, showSuggestions: false } : l)));
    window.addEventListener("scroll", closeAll, true);
    window.addEventListener("resize", closeAll);
    const onClickOutside = (e) => {
      const isInput = Object.values(productInputRefs.current).some((el) => el && el.contains(e.target));
      const isDropdown = e.target.closest?.('[data-product-dropdown-root="true"]');
      if (!isInput && !isDropdown) closeAll();
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      window.removeEventListener("scroll", closeAll, true);
      window.removeEventListener("resize", closeAll);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, []);

  const onProductQueryChange = (lineKey, v) => {
    setLines((prev) => prev.map((l) => (l.key === lineKey ? { ...l, productQuery: v, product_id: "", showSuggestions: true } : l)));
    positionSuggestBox(lineKey);
    clearTimeout(productDebounceRefs.current[lineKey]);
    if (!v.trim()) {
      setLines((prev) => prev.map((l) => (l.key === lineKey ? { ...l, productSuggestions: [] } : l)));
      return;
    }
    productDebounceRefs.current[lineKey] = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/purchase-orders/products/search`, { params: { q: v, limit: 15 } });
        setLines((prev) => prev.map((l) => (l.key === lineKey ? { ...l, productSuggestions: data } : l)));
      } catch { /* silent */ }
    }, 300);
  };

  const pickProduct = (lineKey, p) => {
    setLines((prev) => prev.map((l) => (l.key === lineKey
      ? { ...l, product_id: p.product_id, description: p.description, unit_of_measure: p.unit_of_measure || "EA", productQuery: `${p.product_id} - ${p.description || ""}`, showSuggestions: false }
      : l)));
  };

  const updateLine = (lineKey, field, value) => {
    setLines((prev) => prev.map((l) => (l.key === lineKey ? { ...l, [field]: value } : l)));
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (lineKey) => setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== lineKey) : prev));

  const lineTotal = (l) => (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
  const totalUnits = lines.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
  const grandTotal = lines.reduce((s, l) => s + lineTotal(l), 0);

  const cfg = plant ? PLANT_CONFIG[plant] : null;

  const validationErrors = () => {
    const errors = [];
    if (!plant) errors.push("A Plant (P1 or P2) must be selected");
    if (!poDate) errors.push("PO Date is required");
    if (!attachment?.path) errors.push("The approval email attachment is required");
    if (lines.length === 0) errors.push("At least one line item is required");
    lines.forEach((l, idx) => {
      if (!l.product_id) errors.push(`Line ${idx + 1}: Product must be selected from the list`);
      if (!l.quantity || Number(l.quantity) <= 0) errors.push(`Line ${idx + 1}: Quantity must be greater than 0`);
      if (l.unit_price === "" || Number(l.unit_price) < 0) errors.push(`Line ${idx + 1}: Unit Price must be 0 or more`);
      if (!l.delivery_date) errors.push(`Line ${idx + 1}: Delivery Date is required`);
      if (l.delivery_date && poDate && l.delivery_date < poDate) errors.push(`Line ${idx + 1}: Delivery Date cannot be before PO Date`);
    });
    return errors;
  };

  const checklist = [
    { label: "Plant selected (Purchase Unit, Bill-To & Supplier auto-derived)", ok: !!plant },
    { label: "Approval email attached", ok: !!attachment?.path },
    { label: "Every line matched to a real SAP product, with Qty & Price", ok: lines.every((l) => l.product_id && Number(l.quantity) > 0 && l.unit_price !== "") },
    { label: "Delivery dates on/after PO Date", ok: lines.every((l) => !l.delivery_date || !poDate || l.delivery_date >= poDate) },
  ];
  const canSubmit = checklist.every((c) => c.ok);

  const openConfirm = () => {
    const errors = validationErrors();
    if (errors.length > 0) {
      toast.error(errors[0], { description: errors.length > 1 ? `+${errors.length - 1} more issue(s)` : undefined });
      return;
    }
    setConfirmOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        plant,
        po_date: poDate,
        currency,
        attachment_path: attachment.path,
        attachment_filename: attachment.filename,
        items: lines.map((l) => ({
          product_id: l.product_id, description: l.description || null,
          quantity: Number(l.quantity), unit_of_measure: l.unit_of_measure || "EA",
          unit_price: Number(l.unit_price), delivery_date: l.delivery_date,
        })),
      };
      const { data } = await axios.post(`${API}/interplant-purchase-orders/create`, payload);
      setResult({ po_number: data.po_number, sap_po_number: data.sap_po_number, interplant_pr_number: data.interplant_pr_number });
      setConfirmOpen(false);
      toast.success(`Interplant Purchase Order ${data.po_number} created in SAP`, {
        description: "Click to view its full details",
        action: {
          label: "View PO",
          onClick: () => navigate(`/purchasing-strategy/created-purchase-orders?po=${data.po_number}`),
        },
        duration: 10000,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setResult({ error: typeof detail === "string" && detail.trim() ? detail : "Failed to create Interplant Purchase Order in SAP" });
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setPlant(""); setPoDate(todayISO()); setLines([emptyLine()]); setAttachment(null);
    setResult(null);
  };

  const inputCls = "h-9 text-[13px] rounded-sm border-[#D0D5DD] focus-visible:border-[#004B87] focus-visible:ring-1 focus-visible:ring-[#004B87]";
  const cardCls = "bg-white border border-[#D0D5DD] rounded-sm shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] p-4 space-y-3";
  const sectionHeadingCls = "text-[11px] font-bold uppercase tracking-wide text-[#344054] font-heading mb-1 flex items-center gap-2";

  return (
    <div className="min-h-screen bg-[#F2F4F7] flex flex-col font-sans">
      <Toaster position="top-right" />
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-sm bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Materials Hub</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Interplant Purchase Order Creation</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <main className="flex-1 overflow-auto w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#101828] tracking-tight flex items-center gap-2" data-testid="ipo-page-title">
              <ShieldCheck size={20} className="text-[#004B87]" weight="fill" />
              Interplant Purchase Order Creation
            </h1>
            <p className="text-sm text-[#475467] mt-0.5">Creates a live Interplant Purchase Order in SAP between P1 and P2 - no PR required, submitted the moment you confirm below.</p>
          </div>
          <Badge className="bg-[#EFF8FF] text-[#175CD3] border border-[#B2DDFF] rounded-sm font-data text-xs px-3 py-1.5" data-testid="ipo-live-sap-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[#175CD3] mr-2 inline-block animate-pulse" />
            LIVE SAP OData Write Mode
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* MAIN FORM */}
          <div className="lg:col-span-8 xl:col-span-9 space-y-4">
            {/* 1. Plant selection */}
            <div className={cardCls} data-testid="ipo-plant-card">
              <h2 className={sectionHeadingCls}>
                <Factory size={14} /> 1. Plant Selection
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[#344054]">Plant *</Label>
                  <Select value={plant} onValueChange={setPlant}>
                    <SelectTrigger className={inputCls} data-testid="ipo-plant-select">
                      <SelectValue placeholder="Select P1 or P2..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="P1" data-testid="ipo-plant-option-p1">P1 (Ray International)</SelectItem>
                      <SelectItem value="P2" data-testid="ipo-plant-option-p2">P2 (Radish Technologies)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {cfg && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-[#344054]">Supplier (auto-derived)</Label>
                    <Badge className="h-9 w-full flex items-center justify-start px-3 bg-[#EFF8FF] text-[#175CD3] border border-[#B2DDFF] rounded-sm font-data text-xs" data-testid="ipo-supplier-badge">
                      {cfg.supplierCode} - {cfg.supplierName}
                    </Badge>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-[#98A2B3]">Purchase Unit, Bill-To and Supplier are fixed by the Plant you pick here - Plant P1 buys from Supplier RAD-P2-S, Plant P2 buys from Supplier RAY-P1-S.</p>
            </div>

            {/* 2. Org context */}
            <div className={cardCls} data-testid="ipo-org-context-card">
              <h2 className={sectionHeadingCls}>
                <Buildings size={14} /> 2. Organization &amp; Routing Context
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[#344054]">Purchase Unit (Site)</Label>
                  <div data-testid="ipo-purchase-unit-display">
                    {plant ? (
                      <Badge className="h-9 w-full flex items-center justify-center bg-[#F2F4F7] text-[#004B87] border border-[#D0D5DD] rounded-sm font-data text-xs">
                        {plant} · Auto-derived
                      </Badge>
                    ) : (
                      <div className="h-9 flex items-center px-3 rounded-sm border border-dashed border-[#D0D5DD] bg-[#F9FAFB] text-xs text-[#98A2B3]">
                        Auto-set from Plant
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[#344054]">Company (Business Residence)</Label>
                  <div data-testid="ipo-company-display">
                    {cfg ? (
                      <Badge className="h-9 w-full flex items-center justify-center bg-[#F2F4F7] text-[#004B87] border border-[#D0D5DD] rounded-sm font-data text-xs">
                        {cfg.company} · Auto-derived
                      </Badge>
                    ) : (
                      <div className="h-9 flex items-center px-3 rounded-sm border border-dashed border-[#D0D5DD] bg-[#F9FAFB] text-xs text-[#98A2B3]">
                        Auto-set from Plant
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[#344054]">Bill-To</Label>
                  <div data-testid="ipo-bill-to-display">
                    {cfg ? (
                      <Badge className="h-9 w-full flex items-center justify-center bg-[#F2F4F7] text-[#004B87] border border-[#D0D5DD] rounded-sm font-data text-xs">
                        {cfg.billTo} · Auto-derived
                      </Badge>
                    ) : (
                      <div className="h-9 flex items-center px-3 rounded-sm border border-dashed border-[#D0D5DD] bg-[#F9FAFB] text-xs text-[#98A2B3]">
                        Auto-set from Plant
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Commercial terms */}
            <div className={cardCls} data-testid="ipo-terms-card">
              <h2 className={sectionHeadingCls}>
                <CreditCard size={14} /> 3. Commercial Terms
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[#344054] flex items-center gap-1"><Calendar size={12} /> PO Date *</Label>
                  <Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} className={`${inputCls} font-data`} data-testid="ipo-date-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[#344054]">Currency</Label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className={inputCls} data-testid="ipo-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* 4. Approval attachment (mandatory) */}
            <div className={cardCls} data-testid="ipo-attachment-card">
              <h2 className={sectionHeadingCls}>
                <Paperclip size={14} /> 4. Approval Email Attachment *
              </h2>
              <p className="text-[11px] text-[#98A2B3]">Interplant Purchase Orders have no PR/approval workflow of their own - attach the approval email (PDF, screenshot, or exported .eml/.msg) before you can submit this to SAP.</p>
              {!attachment ? (
                <label className="flex items-center justify-center gap-2 h-20 border-2 border-dashed border-[#D0D5DD] rounded-sm cursor-pointer hover:border-[#004B87] hover:bg-[#F9FAFB] transition-colors" data-testid="ipo-attachment-dropzone">
                  <Paperclip size={16} className="text-[#98A2B3]" />
                  <span className="text-sm text-[#667085]">Click to attach the approval email...</span>
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg,.eml,.msg" className="hidden" onChange={(e) => onAttachmentSelect(e.target.files?.[0])} data-testid="ipo-attachment-input" />
                </label>
              ) : (
                <div className="flex items-center justify-between gap-3 h-12 px-3 border border-[#D0D5DD] rounded-sm bg-[#F9FAFB]" data-testid="ipo-attachment-chip">
                  <span className="flex items-center gap-2 text-sm text-[#344054] min-w-0 truncate">
                    {attachment.uploading ? <CircleNotch size={14} className="animate-spin shrink-0" /> : attachment.error ? <WarningCircle size={14} className="text-[#B42318] shrink-0" /> : <CheckCircle size={14} className="text-[#027A48] shrink-0" weight="fill" />}
                    <span className="truncate">{attachment.filename}</span>
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    {attachment.error && <span className="text-xs text-[#B42318]" data-testid="ipo-attachment-error">{attachment.error}</span>}
                    {attachment.path && (
                      <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs rounded-sm" disabled={viewingAttachment} onClick={viewAttachment} data-testid="ipo-attachment-view-button">
                        {viewingAttachment ? <CircleNotch size={13} className="animate-spin mr-1" /> : <Eye size={13} className="mr-1" />} View
                      </Button>
                    )}
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs rounded-sm" onClick={() => setAttachment(null)} data-testid="ipo-attachment-remove-button">Remove</Button>
                  </div>
                </div>
              )}
            </div>

            {/* 5. Line items */}
            <div className="bg-white border border-[#D0D5DD] rounded-sm shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] overflow-hidden" data-testid="ipo-line-items-card">
              <div className="px-4 py-2.5 border-b border-[#D0D5DD] bg-[#F9FAFB] flex items-center justify-between">
                <h2 className="text-[11px] font-bold uppercase tracking-wide text-[#344054] font-heading flex items-center gap-2">
                  <Truck size={14} /> 5. Line Items
                </h2>
                <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-[11px] rounded-sm border-[#D0D5DD]" onClick={addLine} data-testid="ipo-add-line-button">
                  + Add Line
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] border-collapse min-w-[900px]" data-testid="ipo-line-items-table">
                  <thead>
                    <tr>
                      {["Product", "Qty", "UoM", "Unit Price", "Delivery Date", "Line Total", ""].map((h) => (
                        <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] py-1.5 px-2.5 text-left text-xs font-bold text-[#344054] font-heading uppercase whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, idx) => (
                      <tr key={l.key} className={idx % 2 === 1 ? "bg-[#F9FAFB]" : "bg-white"} data-testid={`ipo-line-row-${idx}`}>
                        <td className="border border-[#D0D5DD] py-1.5 px-2.5 min-w-[280px] relative">
                          <Input
                            ref={(el) => { productInputRefs.current[l.key] = el; }}
                            value={l.productQuery}
                            onChange={(e) => onProductQueryChange(l.key, e.target.value)}
                            onFocus={() => { positionSuggestBox(l.key); setLines((prev) => prev.map((x) => (x.key === l.key ? { ...x, showSuggestions: true } : x))); }}
                            placeholder="Search Product ID or description..."
                            className="h-8 text-xs rounded-sm border-[#D0D5DD] focus-visible:border-[#004B87] focus-visible:ring-1 focus-visible:ring-[#004B87]"
                            data-testid={`ipo-line-product-input-${idx}`}
                          />
                          {l.showSuggestions && l.productSuggestions.length > 0 && suggestBoxPos[l.key] && createPortal(
                            <div
                              className="fixed z-[200] bg-white border border-[#D0D5DD] rounded-sm shadow-xl overflow-y-auto"
                              style={{
                                left: suggestBoxPos[l.key].left, width: suggestBoxPos[l.key].width,
                                maxHeight: suggestBoxPos[l.key].maxHeight,
                                ...(suggestBoxPos[l.key].bottom != null ? { bottom: suggestBoxPos[l.key].bottom } : { top: suggestBoxPos[l.key].top }),
                              }}
                              data-product-dropdown-root="true"
                              data-testid={`ipo-line-product-suggestions-${idx}`}
                            >
                              <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-[#667085] bg-[#F9FAFB] border-b border-[#EAECF0] sticky top-0">
                                {l.productSuggestions.length} match{l.productSuggestions.length === 1 ? "" : "es"} - pick the exact Product ID
                              </div>
                              {l.productSuggestions.map((p) => (
                                <button
                                  key={p.product_id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-xs hover:bg-[#EFF8FF] border-b border-[#EAECF0] last:border-0 flex items-center justify-between gap-3"
                                  onClick={() => pickProduct(l.key, p)}
                                  data-testid={`ipo-line-product-suggestion-${idx}-${p.product_id}`}
                                >
                                  <span className="min-w-0">
                                    <span className="font-semibold text-[#101828] font-data">{p.product_id}</span>
                                    {p.description && <span className="text-[#667085]"> - {p.description}</span>}
                                  </span>
                                  {p.unit_of_measure && (
                                    <span className="shrink-0 text-[10px] font-data font-bold text-[#175CD3] bg-[#EFF8FF] border border-[#B2DDFF] rounded-sm px-1.5 py-0.5">
                                      {p.unit_of_measure}
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>,
                            document.body,
                          )}
                        </td>
                        <td className="border border-[#D0D5DD] py-1.5 px-2.5 min-w-[110px]">
                          <Input type="number" min="0" step="any" value={l.quantity} onChange={(e) => updateLine(l.key, "quantity", e.target.value)} className="h-8 text-xs font-data rounded-sm border-[#D0D5DD] focus-visible:border-[#004B87] focus-visible:ring-1 focus-visible:ring-[#004B87]" data-testid={`ipo-line-qty-input-${idx}`} />
                        </td>
                        <td className="border border-[#D0D5DD] py-1.5 px-2.5 min-w-[100px]">
                          <Input value={l.unit_of_measure} onChange={(e) => updateLine(l.key, "unit_of_measure", e.target.value)} className="h-8 text-xs font-data rounded-sm border-[#D0D5DD] focus-visible:border-[#004B87] focus-visible:ring-1 focus-visible:ring-[#004B87]" data-testid={`ipo-line-uom-input-${idx}`} />
                        </td>
                        <td className="border border-[#D0D5DD] py-1.5 px-2.5 min-w-[110px]">
                          <Input type="number" min="0" step="any" value={l.unit_price} onChange={(e) => updateLine(l.key, "unit_price", e.target.value)} className="h-8 text-xs font-data rounded-sm border-[#D0D5DD] focus-visible:border-[#004B87] focus-visible:ring-1 focus-visible:ring-[#004B87]" data-testid={`ipo-line-price-input-${idx}`} />
                        </td>
                        <td className="border border-[#D0D5DD] py-1.5 px-2.5 min-w-[140px]">
                          <Input type="date" value={l.delivery_date} onChange={(e) => updateLine(l.key, "delivery_date", e.target.value)} className="h-8 text-xs font-data rounded-sm border-[#D0D5DD] focus-visible:border-[#004B87] focus-visible:ring-1 focus-visible:ring-[#004B87]" data-testid={`ipo-line-delivery-date-input-${idx}`} />
                        </td>
                        <td className="border border-[#D0D5DD] py-1.5 px-2.5 min-w-[110px] font-data text-xs font-semibold text-[#101828] whitespace-nowrap" data-testid={`ipo-line-total-${idx}`}>
                          {fmtMoney(lineTotal(l), currency)}
                        </td>
                        <td className="border border-[#D0D5DD] py-1.5 px-2.5 text-center whitespace-nowrap min-w-[60px]">
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-sm hover:bg-[#FEF3F2]" onClick={() => removeLine(l.key)} disabled={lines.length === 1} data-testid={`ipo-line-remove-button-${idx}`} title="Remove row">
                            <Trash size={13} className="text-[#B42318]" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* SUMMARY SIDEBAR */}
          <div className="lg:col-span-4 xl:col-span-3 space-y-4">
            <div className="sticky top-4 space-y-4">
              <div className={cardCls} data-testid="ipo-summary-card">
                <h2 className={sectionHeadingCls}>
                  <ListChecks size={14} /> 6. Order Summary
                </h2>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-[#667085] text-[13px]">Line Items</span>
                    <span className="font-data font-semibold text-[#101828]" data-testid="ipo-summary-line-count">{lines.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#667085] text-[13px]">Total Units</span>
                    <span className="font-data font-semibold text-[#101828]" data-testid="ipo-summary-total-units">{totalUnits}</span>
                  </div>
                  <div className="h-px bg-[#EAECF0] my-1" />
                  <div className="flex items-center justify-between">
                    <span className="text-[#344054] font-semibold text-[13px]">Gross Order Value</span>
                    <span className="font-data font-extrabold text-base text-[#004B87]" data-testid="ipo-summary-total-value">{fmtMoney(grandTotal, currency)}</span>
                  </div>
                </div>

                <div className="h-px bg-[#EAECF0]" />

                <div className="space-y-1.5" data-testid="ipo-summary-checklist">
                  {checklist.map((c, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs" data-testid={`ipo-checklist-item-${i}`}>
                      {c.ok ? <CheckCircle size={14} className="text-[#027A48] mt-0.5 shrink-0" weight="fill" /> : <WarningCircle size={14} className="text-[#B54708] mt-0.5 shrink-0" weight="fill" />}
                      <span className={c.ok ? "text-[#344054]" : "text-[#B54708]"}>{c.label}</span>
                    </div>
                  ))}
                </div>

                <Button
                  type="button"
                  className="w-full h-10 rounded-sm bg-[#004B87] hover:bg-[#003A6A] active:bg-[#00294D] font-semibold"
                  onClick={openConfirm}
                  disabled={!canSubmit}
                  data-testid="ipo-submit-button"
                >
                  Create Interplant PO in SAP <ArrowRight size={15} className="ml-1.5" />
                </Button>
                <p className="text-[11px] text-[#98A2B3] text-center">This creates a real, live SAP document. Review the audit recap carefully.</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Confirm dialog - audit recap */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-2xl" data-testid="ipo-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><WarningCircle size={18} className="text-[#B54708]" /> Audit Recap - Confirm Interplant Purchase Order</DialogTitle>
            <DialogDescription>This will submit a real Purchase Order to SAP ByDesign - it cannot be undone from this app.</DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-3 text-[#344054]">
            <div className="grid grid-cols-2 gap-2 bg-[#F9FAFB] rounded-sm p-3 font-data text-xs">
              <div><span className="text-[#667085]">Plant:</span> <b>{plant}</b></div>
              <div><span className="text-[#667085]">Company:</span> <b>{cfg?.company}</b></div>
              <div><span className="text-[#667085]">Purchase Unit:</span> <b>{plant ? `${plant}-PUR` : "-"}</b></div>
              <div><span className="text-[#667085]">Bill-To:</span> <b>{cfg?.billTo}</b></div>
              <div><span className="text-[#667085]">PO Date:</span> <b>{poDate}</b></div>
              <div><span className="text-[#667085]">Attachment:</span> <b data-testid="ipo-confirm-attachment">{attachment?.filename || "-"}</b></div>
              <div className="col-span-2">
                <span className="text-[#667085]">Supplier:</span>{" "}
                <b data-testid="ipo-confirm-supplier">{cfg ? `${cfg.supplierCode} - ${cfg.supplierName}` : "-"}</b>
              </div>
            </div>
            <p className="text-[11px] text-[#98A2B3]">A reference number (e.g. IP001) will be auto-generated and written into SAP's PR field on this PO - there's no real PR for an interplant order.</p>
            <div className="border border-[#D0D5DD] rounded-sm overflow-hidden">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-[#EAECF0]">
                    <th className="text-left p-2 font-heading uppercase text-[#344054]">Product</th>
                    <th className="text-right p-2 font-heading uppercase text-[#344054]">Qty</th>
                    <th className="text-left p-2 font-heading uppercase text-[#344054]">Delivery</th>
                    <th className="text-right p-2 font-heading uppercase text-[#344054]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.key} className="border-t border-[#D0D5DD]">
                      <td className="p-2 font-data">{l.product_id}</td>
                      <td className="p-2 text-right font-data">{l.quantity} {l.unit_of_measure}</td>
                      <td className="p-2 font-data">{l.delivery_date}</td>
                      <td className="p-2 text-right font-data font-semibold">{fmtMoney(lineTotal(l), currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end font-data text-sm font-bold text-[#004B87]">Grand Total: {fmtMoney(grandTotal, currency)}</div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setConfirmOpen(false)} disabled={submitting} data-testid="ipo-confirm-cancel-button">Cancel</Button>
            <Button type="button" className="rounded-sm bg-[#004B87] hover:bg-[#003A6A] active:bg-[#00294D]" onClick={submit} disabled={submitting} data-testid="ipo-confirm-submit-button">
              {submitting ? <><CircleNotch size={13} className="mr-1.5 animate-spin" /> Submitting to SAP...</> : "Confirm & Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result dialog */}
      <Dialog open={!!result} onOpenChange={(open) => !open && resetForm()}>
        <DialogContent data-testid="ipo-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {result?.po_number ? <CheckCircle size={18} className="text-[#027A48]" weight="fill" /> : <WarningCircle size={18} className="text-[#B42318]" weight="fill" />}
              {result?.po_number ? "Interplant Purchase Order Created" : "Interplant Purchase Order Failed"}
            </DialogTitle>
          </DialogHeader>
          {result?.po_number ? (
            <p className="text-sm text-[#344054]" data-testid="ipo-result-success-message">
              SAP Purchase Order{" "}
              <button
                type="button"
                className="font-data font-bold text-[#004B87] underline hover:text-[#003A6A]"
                data-testid="ipo-result-number"
                onClick={() => navigate(`/purchasing-strategy/created-purchase-orders?po=${result.po_number}`)}
              >
                {result.po_number}
              </button>{" "}
              was created successfully.
              {result.sap_po_number && (
                <span className="block mt-1 text-xs text-[#475467]" data-testid="ipo-result-printed-number">
                  Printed PO #: <span className="font-data font-semibold">{result.sap_po_number}</span>
                </span>
              )}
              {result.interplant_pr_number && (
                <span className="block mt-1 text-xs text-[#475467]" data-testid="ipo-result-pr-number">
                  SAP PR field: <span className="font-data font-semibold">{result.interplant_pr_number}</span>
                </span>
              )}
            </p>
          ) : (
            <p className="text-sm text-[#B42318]" data-testid="ipo-result-error-message">{result?.error}</p>
          )}
          <DialogFooter>
            {result?.po_number && (
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => navigate(`/purchasing-strategy/created-purchase-orders?po=${result.po_number}`)} data-testid="ipo-result-view-created-button">
                View Created POs
              </Button>
            )}
            <Button type="button" className="rounded-sm bg-[#004B87] hover:bg-[#003A6A] active:bg-[#00294D]" onClick={resetForm} data-testid="ipo-result-close-button">
              {result?.po_number ? "Create Another" : "Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
